export const locales = ["es", "en", "pt"] as const;
export const defaultLocale = "es";

export type Locale = (typeof locales)[number];

export function isLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value);
}

/**
 * El portugués se ofrece sólo en las secciones de pasajero. Las de corporativo
 * (noticias, transparencia, licitaciones, memorias) siguen en es/en porque su
 * contenido vive en el CMS, que todavía no publica pt: ver `cmsLang`.
 */
const CORPORATE_PREFIXES = ["/corporativo"] as const;

/**
 * Portales de los aeropuertos regionales. Sus textos viven en los configs de
 * cada aeropuerto (`app/[locale]/mppa/config.ts` y compañía), que sólo traen
 * es/en —ver `LocalizedText`—, así que tampoco ofrecen portugués. `/aeropuertos`
 * queda fuera a propósito: esa página sí está traducida a pt.
 */
const REGIONAL_PREFIXES = ["/mppa", "/mpda", "/mpsm"] as const;

function matchesPrefix(prefixes: readonly string[], path: string): boolean {
  return prefixes.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/** Rutas —ya sin el segmento de locale— donde no ofrecemos portugués. */
export function localeIsAvailableFor(locale: Locale, path: string): boolean {
  if (locale !== "pt") return true;
  return !matchesPrefix([...CORPORATE_PREFIXES, ...REGIONAL_PREFIXES], path);
}

/** ¿La ruta pertenece al portal de un aeropuerto regional? */
export function isRegionalPath(path: string): boolean {
  return matchesPrefix(REGIONAL_PREFIXES, path);
}

/** Quita el segmento de locale inicial de un pathname ("/en/vuelos" → "/vuelos"). */
export function stripLocale(pathname: string): string {
  const [, first, ...rest] = pathname.split("/");
  if (first && isLocale(first)) return `/${rest.join("/")}`;
  return pathname || "/";
}

/**
 * Varios backends y estructuras de datos sólo existen en es/en:
 *  - el CMS (cms.tocumenaero.com): pedirle `lang=pt` no da error, devuelve
 *    español en silencio, así que el mapeo tiene que ser explícito;
 *  - Lost & Found, que sólo trae `name_es` / `name_en`;
 *  - los configs de los aeropuertos regionales.
 *
 * Excepción: atencion.tocumenaero.com sí publica portugués y recibe el locale
 * tal cual (ver fetchBuzonSugerencia).
 */
export const esEnLocales = ["es", "en"] as const;
export type EsEnLocale = (typeof esEnLocales)[number];

/**
 * Reduce un locale de la web a es/en. Para un lusohablante el español es más
 * inteligible que el inglés, así que pt cae a es.
 */
export function toEsEn(locale: Locale): EsEnLocale {
  return locale === "en" ? "en" : "es";
}

/**
 * Idioma para el CMS. Cuando publique portugués, este es el único punto a
 * cambiar: devolver `locale` en vez de reducirlo.
 */
export const cmsLang = toEsEn;

/** Etiqueta BCP-47 para Intl (fechas, horas, números). */
export function intlTag(locale: Locale): string {
  switch (locale) {
    case "en":
      return "en-US";
    case "pt":
      return "pt-BR";
    default:
      return "es-PA";
  }
}

/**
 * Idioma para las URLs de LivingMap. El visor acepta el parámetro sin validar
 * (cualquier valor responde 200 y cae a su idioma por defecto), así que si el
 * mapa no aparece en portugués hay que confirmarlo con LivingMap.
 */
export function livingMapLang(locale: Locale): string {
  switch (locale) {
    case "en":
      return "en-GB";
    case "pt":
      return "pt-BR";
    default:
      return "es-ES";
  }
}
